import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, LayoutGrid, Search, ShoppingCart, User } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function MobileBottomNav() {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Auto-hide on scroll down, reappear on scroll up (same pattern as the header)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;
      if (y < 80) {
        setIsHidden(false);
      } else if (delta > 6) {
        setIsHidden(true);
      } else if (delta < -6) {
        setIsHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { pathname } = useLocation();
  const { itemCount } = useCart();
  const { user } = useAuth();
  const { data: cats } = useQuery({
    queryKey: ["consumables-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consumables_categories")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });
  const firstCat = (cats as any[] | undefined)?.[0]?.slug as string | undefined;

  const item = "flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors";
  const isActive = (p: string) => (p === "/" ? pathname === "/" : pathname.includes(p));
  const cls = (p: string) => cn(item, isActive(p) ? "text-primary" : "text-muted-foreground");

  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)] lg:hidden transition-transform duration-300 will-change-transform",
        isHidden ? "translate-y-full" : "translate-y-0"
      )}
    >
      <Link to="/" className={cls("/")}><Home className="h-5 w-5" />Início</Link>
      <Link to={firstCat ? `/consumiveis/${firstCat}` : "/consumiveis"} className={cls("/consumiveis")}><LayoutGrid className="h-5 w-5" />Consumíveis</Link>
      <button
        type="button"
        onClick={() => window.dispatchEvent(new Event("open-mobile-search"))}
        className={cn(item, "text-muted-foreground")}
      >
        <Search className="h-5 w-5" />Pesquisar
      </button>
      <Link to="/carrinho" className={cls("/carrinho")}>
        <span className="relative">
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {itemCount > 99 ? "99+" : itemCount}
            </span>
          )}
        </span>
        Carrinho
      </Link>
      <Link to={user ? "/conta" : "/auth"} className={cls(user ? "/conta" : "/auth")}>
        <User className="h-5 w-5" />{user ? "Conta" : "Entrar"}
      </Link>
    </nav>
  );
}
